
/**
 * Mobile friendly overview of each assessment row and the absence data.
 * Absence data is fetched from the "/api/assessments" endpoint.
 */
import React, { useEffect, useState } from "react";
import "./App.css";

type Assessment = {
  id?: string | number;
  name?: string;
  title?: string;
  subTitle?: string;
  color?: string;
  friendlyUpdatedAt?: string;
  published?: number;
  read?: number;
  latestAbsence?: {
    id?: number;
    holisticAssessmentId?: number;
    lessons?: number;
    attendance?: number; // percent
    absence?: number; // percent
    reportedAbsence?: number; // percent
    displayName?: string;
    fetchedAt?: number; // epoch ms
    [key: string]: any;
  } | null;
  [key: string]: any;
};

export function App() {
  const [assessments, setAssessments] = useState<Assessment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // fetch assessments (exposed so we can refresh after manual sync)
  async function fetchAssessments() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/assessments");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) setAssessments(data);
      else if (data && Array.isArray(data.assessments)) setAssessments(data.assessments);
      else setAssessments([]);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    fetchAssessments();
    return () => { mounted = false; };
  }, []);

  function renderTimestamp(ts?: string | number) {
    if (ts === null || ts === undefined) return "-";
    const d = typeof ts === "number" ? new Date(ts) : new Date(String(ts));
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString("sv-SE", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  function fmtPercent(n?: number) {
    if (n === null || n === undefined) return "—";
    return `${Number(n).toFixed(Number.isInteger(n) ? 0 : 1)}%`;
  }

  const sortedAssessments = assessments
    ? [...assessments].sort((a, b) => {
        const ta = new Date(a.updatedAt ?? a.friendlyUpdatedAt ?? 0).getTime();
        const tb = new Date(b.updatedAt ?? b.friendlyUpdatedAt ?? 0).getTime();
        return tb - ta;
      })
    : null;

  const lastFetchedAssessment = sortedAssessments
    ? sortedAssessments.reduce<Assessment | null>((best, cur) => {
        const bestTs = best?.latestAbsence?.fetchedAt ?? 0;
        const curTs = cur?.latestAbsence?.fetchedAt ?? 0;
        return curTs > bestTs ? cur : best;
      }, null)
    : null;

  // compute averages across assessments that have latestAbsence
  const assessmentsWithAbsence = assessments ? assessments.filter((a) => !!a.latestAbsence) : [];
  const countWithAbsence = assessmentsWithAbsence.length;
  const avgAttendance = countWithAbsence
    ? assessmentsWithAbsence.reduce((s, a) => s + Math.max(0, Math.min(100, Number(a.latestAbsence?.attendance ?? 0))), 0) / countWithAbsence
    : null;
  const avgAbsence = countWithAbsence
    ? assessmentsWithAbsence.reduce((s, a) => s + Math.max(0, Math.min(100, Number(a.latestAbsence?.absence ?? 0))), 0) / countWithAbsence
    : null;
  const avgReported = countWithAbsence
    ? assessmentsWithAbsence.reduce((s, a) => s + Math.max(0, Math.min(100, Number(a.latestAbsence?.reportedAbsence ?? 0))), 0) / countWithAbsence
    : null;

  function fmtAvg(n?: number | null) {
    if (n === null || n === undefined) return "—";
    return fmtPercent(Number(n));
  }

  async function handleManualSync() {
    setSyncing(true);
    try {
      const resp = await fetch("/api/sync", { method: "POST" });
      if (!resp.ok) throw new Error(`Sync failed ${resp.status}`);
      // refresh assessments after successful sync
      await fetchAssessments();
    } catch (err) {
      console.error(err);
      setError(String(err));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="tw-app">
      <header className="tw-header">
        <h1>Frånvaroöversikt</h1>
      </header>

      <main className="tw-main">
        {loading && <div className="tw-empty">Loading…</div>}
        {error && <div className="tw-error">Error: {error}</div>}

        {!loading && assessments && assessments.length === 0 && (
          <div className="tw-empty">No assessments found</div>
        )}

        {assessments && assessments.length > 0 && (
          <div className="tw-summary">
            <div className="tw-summary-item">
              <div className="tw-summary-label">Närvaro</div>
              <div className="tw-summary-value" style={{ color: "#16a34a" }}>{fmtAvg(avgAttendance)}</div>
            </div>
            <div className="tw-summary-item">
              <div className="tw-summary-label">Frånvaro</div>
              <div className="tw-summary-value" style={{ color: "#ef4444" }}>{fmtAvg(avgAbsence)}</div>
            </div>
            <div className="tw-summary-item">
              <div className="tw-summary-label">Anmäld frånvaro</div>
              <div className="tw-summary-value" style={{ color: "#f59e0b" }}>{fmtAvg(avgReported)}</div>
            </div>
          </div>
        )}

        <ul className="tw-list">
          {sortedAssessments &&
            sortedAssessments.map((a) => {
              const title = a.title ?? a.name ?? `Assessment ${a.id ?? ""}`;
              const sub = a.subTitle ?? "";
              const friendly = a.friendlyUpdatedAt ?? a.updatedAt ?? null;
              const absence = a.latestAbsence;
              const attPct = Math.max(0, Math.min(100, Number(absence?.attendance ?? 0)));
              const absPct = Math.max(0, Math.min(100, Number(absence?.absence ?? 0)));
              const repPct = Math.max(0, Math.min(100, Number(absence?.reportedAbsence ?? 0)));

              return (
                <li key={String(a.id ?? title)} className="tw-card">
                  <div className="tw-card-row">
                    <div className="tw-left">
                      <div className="tw-swatch" style={{ background: a.color ?? "#ddd" }} />
                      <div style={{ minWidth: 0 }}>
                        <div className="tw-card-title">{title}</div>
                        <div className="tw-card-sub" style={{ marginTop: 4 }}>{sub}</div>
                        <div className="tw-meta">{friendly}</div>
                      </div>
                    </div>
                  </div>

                  {absence ? (
                    <div className="tw-absence">
                      <div className="tw-attendance-bar" aria-hidden>
                        <div className="tw-bar-seg att" style={{ width: `${attPct}%` }}>
                          {attPct >= 10 ? <span className="tw-bar-label">{fmtPercent(attPct)}</span> : null}
                        </div>
                        <div className="tw-bar-seg abs" style={{ width: `${absPct}%` }}>
                          {absPct >= 10 ? <span className="tw-bar-label">{fmtPercent(absPct)}</span> : null}
                        </div>
                        <div className="tw-bar-seg rep" style={{ width: `${repPct}%` }}>
                          {repPct >= 10 ? <span className="tw-bar-label">{fmtPercent(repPct)}</span> : null}
                        </div>
                      </div>

                      <div className="tw-absence-row">
                        <div className="tw-absence-label">Lektioner</div>
                        <div className="tw-absence-value">{absence.lessons ?? "—"}</div>
                      </div>

                      {/* fetched timestamp removed from cards; shown in footer instead */}
                    </div>
                  ) : (
                    <div className="tw-card-sub">Ingen frånvarodata</div>
                  )}
                </li>
              );
            })}
        </ul>
      </main>
      <footer className="tw-footer">
        {lastFetchedAssessment && lastFetchedAssessment.latestAbsence ? (
          <div>
            Senast ändrad: {renderTimestamp(lastFetchedAssessment.latestAbsence.fetchedAt)}
            <button className="tw-refresh-button" onClick={handleManualSync} disabled={syncing} aria-label="Uppdatera">
              {syncing ? "Uppdaterar…" : "↻"}
            </button>
          </div>
        ) : (
          <div>
            Inga hämtade bedömningar ännu
            <button className="tw-refresh-button" onClick={handleManualSync} disabled={syncing} aria-label="Uppdatera">
              {syncing ? "Uppdaterar…" : "↻"}
            </button>
          </div>
        )}
      </footer>
    </div>
  );
}

export default App;
